import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtMoney } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, btnPrimary, btnSecondary, btnDanger } from "../components/Modal";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", contact: "", phone: "", whatsapp: "", email: "", address: "", products: "" };

export default function Suppliers() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Fournisseur modifié" : "Fournisseur créé");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/suppliers/${confirmDel.id}`);
      toast.success("Fournisseur supprimé");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "name", label: "Fournisseur", render: (s) => (
      <div><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.products}</div></div>) },
    { key: "contact", label: "Contact" },
    { key: "phone", label: "Téléphone" },
    { key: "total_purchased", label: "Total acheté", className: "text-right", render: (s) => <span className="font-money">{fmtMoney(s.total_purchased, cur)}</span> },
    { key: "balance", label: "Dette", className: "text-right", render: (s) => (
      <span className={`font-money font-bold ${s.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtMoney(s.balance, cur)}</span>) },
    { key: "actions", label: "", className: "text-right", render: (s) => (
      <div className="flex justify-end gap-1">
        {hasPerm("suppliers.edit") && <button data-testid={`edit-supplier-${s.id}`} onClick={() => openEdit(s)} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"><Pencil className="h-4 w-4" /></button>}
        {hasPerm("suppliers.delete") && <button data-testid={`delete-supplier-${s.id}`} onClick={() => setConfirmDel(s)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950"><Trash2 className="h-4 w-4" /></button>}
      </div>) },
  ];

  return (
    <div className="space-y-4" data-testid="suppliers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Fournisseurs</h1>
        {hasPerm("suppliers.create") && <button data-testid="add-supplier-btn" onClick={openNew} className={btnPrimary}><Plus className="h-4 w-4" /> Nouveau fournisseur</button>}
      </div>
      <DataTable testid="suppliers-table" columns={columns} rows={rows} searchKeys={["name", "contact", "phone", "products"]} />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Modifier le fournisseur" : "Nouveau fournisseur"}>
        <div className="space-y-3">
          <Field label="Raison sociale *"><input data-testid="sup-name" value={form.name} onChange={set("name")} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact"><input data-testid="sup-contact" value={form.contact} onChange={set("contact")} className={inputCls} /></Field>
            <Field label="Téléphone"><input data-testid="sup-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
            <Field label="WhatsApp"><input data-testid="sup-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} /></Field>
            <Field label="Email"><input data-testid="sup-email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
          </div>
          <Field label="Adresse"><input data-testid="sup-address" value={form.address} onChange={set("address")} className={inputCls} /></Field>
          <Field label="Produits fournis"><input data-testid="sup-products" value={form.products} onChange={set("products")} className={inputCls} placeholder="Ciment, fer, tôles..." /></Field>
          <button data-testid="save-supplier" onClick={save} disabled={!form.name} className={`${btnPrimary} w-full`}>{editing ? "Enregistrer" : "Créer"}</button>
        </div>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Supprimer le fournisseur">
        <p className="text-sm">Supprimer <b>{confirmDel?.name}</b> ?</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-delete-supplier" onClick={del} className={`${btnDanger} flex-1`}>Supprimer</button>
          <button onClick={() => setConfirmDel(null)} className={btnSecondary}>Annuler</button>
        </div>
      </Modal>
    </div>
  );
}
